import { apiClient } from './apiClient';

const PHOTO_DB_NAME = 'ipys-offline-photo-db';
const PHOTO_STORE_NAME = 'photoUploads';
const PHOTO_DB_VERSION = 1;
const PHOTO_QUEUE_EVENT = 'ipys-offline-photo-queue-updated';

const isBrowser = () => typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

const emitPhotoQueueEvent = async () => {
  if (!isBrowser()) return;
  const count = await getOfflinePhotoQueueCount();
  window.dispatchEvent(
    new CustomEvent(PHOTO_QUEUE_EVENT, {
      detail: { count },
    })
  );
};

const openPhotoDb = () =>
  new Promise((resolve, reject) => {
    if (!isBrowser()) {
      reject(new Error('IndexedDB kullanilamiyor.'));
      return;
    }

    const request = indexedDB.open(PHOTO_DB_NAME, PHOTO_DB_VERSION);
    request.onerror = () => reject(request.error || new Error('IndexedDB açılamadı.'));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PHOTO_STORE_NAME)) {
        const store = db.createObjectStore(PHOTO_STORE_NAME, { keyPath: 'id' });
        store.createIndex('created_at', 'created_at', { unique: false });
      }
    };
  });

const withStore = async (mode, callback) => {
  const db = await openPhotoDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(PHOTO_STORE_NAME, mode);
    const store = transaction.objectStore(PHOTO_STORE_NAME);
    let callbackResult;

    transaction.oncomplete = () => {
      db.close();
      resolve(callbackResult);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error || new Error('İşlem tamamlanamadı.'));
    };
    transaction.onabort = () => {
      db.close();
      reject(transaction.error || new Error('Islem iptal edildi.'));
    };

    Promise.resolve(callback(store, transaction))
      .then((value) => {
        callbackResult = value;
      })
      .catch((error) => {
        db.close();
        reject(error);
      });
  });
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

export const listOfflinePhotoQueue = async () => {
  if (!isBrowser()) return [];
  const items = await withStore('readonly', (store) =>
    new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
      request.onerror = () => reject(request.error || new Error('Kuyruk okunamadı.'));
    })
  );

  return items.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
};

export const getOfflinePhotoQueueCount = async () => {
  const items = await listOfflinePhotoQueue();
  return items.length;
};

export const enqueueOfflinePhotoUpload = async ({ url, headers, fields, file }) => {
  if (!file) {
    throw new Error('Dosya seçilmedi.');
  }

  const fileDataUrl = await fileToDataUrl(file);
  const record = {
    id: `photo-${Date.now()}-${Math.round(Math.random() * 100000)}`,
    method: 'post',
    url,
    headers: headers || {},
    label: `Fotoğraf bekliyor: ${fields?.description || file.name}`,
    fields: fields || {},
    file_name: file.name,
    file_type: file.type || 'image/jpeg',
    file_data_url: fileDataUrl,
    created_at: new Date().toISOString(),
  };

  await withStore('readwrite', (store) => {
    store.put(record);
  });
  await emitPhotoQueueEvent();
  return record;
};

export const deleteOfflinePhotoUpload = async (id) => {
  await withStore('readwrite', (store) => {
    store.delete(id);
  });
  await emitPhotoQueueEvent();
};

export const processOfflinePhotoQueue = async () => {
  if (!isBrowser() || !window.navigator.onLine) {
    return { processed: 0, remaining: await getOfflinePhotoQueueCount() };
  }

  const queue = await listOfflinePhotoQueue();
  if (queue.length === 0) {
    return { processed: 0, remaining: 0 };
  }

  let processed = 0;
  for (const item of queue) {
    try {
      const blob = await dataUrlToBlob(item.file_data_url);
      const uploadFormData = new FormData();
      uploadFormData.append('photo', blob, item.file_name || 'offline-photo.jpg');

      Object.entries(item.fields || {}).forEach(([key, value]) => {
        uploadFormData.append(key, value == null ? '' : String(value));
      });

      await apiClient.post(item.url, uploadFormData, {
        headers: {
          ...(item.headers || {}),
        },
      });

      await withStore('readwrite', (store) => {
        store.delete(item.id);
      });
      processed += 1;
    } catch (error) {
      if (!error.response) {
        break;
      }
    }
  }

  await emitPhotoQueueEvent();
  return { processed, remaining: await getOfflinePhotoQueueCount() };
};

export const subscribeOfflinePhotoQueue = (listener) => {
  if (!isBrowser()) return () => {};
  const wrapped = (event) => listener(Number(event?.detail?.count || 0));
  window.addEventListener(PHOTO_QUEUE_EVENT, wrapped);
  return () => window.removeEventListener(PHOTO_QUEUE_EVENT, wrapped);
};
